import { DeleteOutline } from '@mui/icons-material';
import { Grid, IconButton, Box } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import { groupedFieldsStyle } from '../../../TransitionStyles';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormDatePickerField from '../../../FormFields/FormDatePickerField';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { FILE_TYPES } from '../../../formFileTypesEnum';

Equipment.propTypes = {
  isEditable: PropTypes.bool,
  equipmentsFieldName: PropTypes.string,
};

export default function Equipment({ isEditable, equipmentsFieldName }) {
  const [focusedField, setFocusedField] = useState(null);
  const { control, setValue } = useFormContext();
  const {
    data: { isCanceled },
  } = useSelector((state) => state.transitions);

  const disabled = !isEditable || isCanceled;

  const { fields, append } = useFieldArray({
    control,
    name: equipmentsFieldName,
    keyName: '_id',
  });

  const watchedValues = useWatch({ control, name: equipmentsFieldName }) || [];

  const handleDelete = (idx) => {
    setValue(`${equipmentsFieldName}[${idx}].deleteFlag`, true, {
      shouldDirty: true,
    });
  };

  const visibleRows = fields.map((field, idx) => ({ field, idx })).filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  return (
    <>
      {visibleRows.map(({ field, idx }) => (
        <Grid container item xs={12} wrap="nowrap" key={field._id} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs={visibleRows.length > 1 ? 11.5 : 12}>
            <Box sx={{ backgroundColor: '#f1f3f4cc', ...groupedFieldsStyle }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Type' }} />
                    <FormTextField
                      fieldName={`${equipmentsFieldName}[${idx}].type`}
                      fieldAttributes={{ isEditable: !disabled }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Make' }} />
                    <FormTextField
                      fieldName={`${equipmentsFieldName}[${idx}].make`}
                      fieldAttributes={{ isEditable: !disabled }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Model' }} />
                    <FormTextField
                      fieldName={`${equipmentsFieldName}[${idx}].model`}
                      fieldAttributes={{ isEditable: !disabled }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Serial number' }} />
                    <FormTextField
                      fieldName={`${equipmentsFieldName}[${idx}].serialNumber`}
                      fieldAttributes={{ isEditable: !disabled }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Date purchased' }} />
                    <FormDatePickerField
                      fieldName={`${equipmentsFieldName}[${idx}].purchaseDate`}
                      fieldAttributes={{ isEditable: !disabled }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                      maxDate={dayjs().startOf('day').toDate()}
                    />
                  </FieldContainer>
                </Grid>

                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Upload picture', fieldType: 'file' }} />
                    <FormFileDrop
                      fieldName={`${equipmentsFieldName}[${idx}].pictures`}
                      fieldAttributes={{
                        isEditable: !disabled,
                        fileTypeId: FILE_TYPES.HOUSEKEEPING_EQUIPMENT_PICTURE,
                        acceptedFileTypes: { 'image/*': [] },
                      }}
                    />
                  </FieldContainer>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {visibleRows.length > 1 && (
            <Grid item xs={0.5}>
              <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                <DeleteOutline />
              </IconButton>
            </Grid>
          )}
        </Grid>
      ))}

      <AddMoreButton
        buttonLabel="Add more"
        handleAddMoreClick={() =>
          append({
            id: 0,
            type: null,
            make: null,
            model: null,
            serialNumber: null,
            purchaseDate: null,
            pictures: [],
          })
        }
        disabled={disabled}
      />
    </>
  );
}
